import { ChangeEvent, useRef } from "react"
import { alpha, Box, IconButton, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faImage, faTrash } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { PhotoPreviewTooltip } from "../../../../../../../shared/components/photos/PhotoPreviewTooltip"

import { localPhotoFromFile } from "../../weekMenuLib"
import { WeekMenuPhoto } from "../../weekMenuTypes"
import { PlannerTag, PlannerTagVariant } from "../PlannerTag"

const useStyles = makeStyles()((theme) => ({
    addButton: {
        alignSelf: "flex-start",
        "&:hover": {
            borderColor: theme.palette.tailwind.slate[600],
        },
    },
    addButtonError: {
        borderColor: theme.palette.tailwind.amber[600],
        background: alpha(theme.palette.common.black, 0.02),
    },
    photoRow: {
        flexShrink: 0,
    },
    thumb: {
        width: "1.5rem",
        height: "1.5rem",
        borderRadius: ".25rem",
        objectFit: "cover",
        flexShrink: 0,
    },
    thumbPlaceholder: {
        width: "1.5rem",
        height: "1.5rem",
        borderRadius: ".25rem",
        background: theme.palette.tailwind.gray[100],
        color: theme.palette.tailwind.gray[400],
        flexShrink: 0,
        "& svg": {
            fontSize: ".75rem",
        },
    },
    fileInput: {
        display: "none",
    },
    previewCaption: {
        color: theme.palette.tailwind.gray[600],
    },
    previewDeleteButton: {
        width: "1.5rem",
        height: "1.5rem",
        borderRadius: ".25rem",
        color: theme.palette.tailwind.gray[500],
        "& svg": {
            fontSize: ".875rem",
        },
        "&:hover": {
            color: theme.palette.error.main,
        },
    },
}))

type Props = Readonly<{
    photo: WeekMenuPhoto | null
    hasError: boolean
    isLocked: boolean
    onChange: (photo: WeekMenuPhoto | null) => void
}>

export function CellImageField({ photo, hasError, isLocked, onChange }: Props) {
    const { classes, cx } = useStyles()
    const fileInputRef = useRef<HTMLInputElement | null>(null)

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return
        onChange(localPhotoFromFile(file))
        event.target.value = ""
    }

    const handleDelete = () => {
        onChange(null)
    }

    if (!photo) {
        // A locked field the template left empty has nothing to show and nothing to upload.
        if (isLocked) return null

        return (
            <>
                <PlannerTag
                    variant={PlannerTagVariant.ADD}
                    className={cx(classes.addButton, hasError && classes.addButtonError)}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <LocalizedStrict id="merchant-week-menu-cell-add-image-label">
                        <span>Photo</span>
                    </LocalizedStrict>
                </PlannerTag>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className={classes.fileInput}
                    onChange={handleFileChange}
                />
            </>
        )
    }

    const photoRow = (
        <Box className={classes.photoRow}>
            {photo.url ? (
                <img src={photo.url} alt={photo.name} className={classes.thumb} />
            ) : (
                <Stack alignItems="center" justifyContent="center" className={classes.thumbPlaceholder}>
                    <FontAwesomeIcon icon={faImage} />
                </Stack>
            )}
        </Box>
    )

    const isReadOnly = photo.isFromTemplate || isLocked

    return (
        <PhotoPreviewTooltip
            url={photo.url}
            name={photo.name}
            nameAction={
                !isReadOnly && (
                    <LocalizedStrict id="merchant-week-menu-cell-image-delete-button" attrs={{ "aria-label": true }}>
                        <IconButton
                            size="small"
                            aria-label="Delete"
                            className={classes.previewDeleteButton}
                            onClick={handleDelete}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </IconButton>
                    </LocalizedStrict>
                )
            }
            footer={
                isReadOnly && (
                    <LocalizedStrict id="merchant-week-menu-cell-image-from-template-label">
                        <Typography variant="caption" className={classes.previewCaption}>
                            Picture from template
                        </Typography>
                    </LocalizedStrict>
                )
            }
        >
            {photoRow}
        </PhotoPreviewTooltip>
    )
}
